import { alertApiRef, useApi } from '@mainstage/core-plugin-api';
import {
  WITTY_ASSISTANT_AGENTS_QUERY_KEY,
  wittyAssistantApiRef,
  WittyAssistantAgentSummary,
  WittySessionSummary,
} from '../api';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { flushSync } from 'react-dom';
import {
  useEnvironmentsContext,
  WittyMessage,
} from '@agilelab/plugin-wb-platform';
import {
  WittyToolCall,
  WittyUnavailableMcpServer,
} from '@agilelab/plugin-wb-witty-common';
import { handleWittyError } from '../hooks/handleWittyError';
import { getCurrentPageContext } from './AgentContext';
import { useQuery } from '@tanstack/react-query';

export type WittyAssistantHook = {
  messages: WittyMessage[];
  open: boolean;
  loading: boolean;
  generating: boolean;
  statusText: string;
  activeToolCalls: WittyToolCall[];
  unavailableMcpServers: WittyUnavailableMcpServer[];
  onOpenMcpConnection: (connectionId: string) => void;
  onNewMessage: (message: WittyMessage) => void;
  onStop: () => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
  expanded: boolean;
  setExpanded: Function;
  onNewSession: Function;
  historyOpen: boolean;
  setHistoryOpen: Dispatch<SetStateAction<boolean>>;
  sessions: WittySessionSummary[];
  sessionsLoading: boolean;
  loadSessions: () => void;
  loadMoreSessions: () => void;
  hasMoreSessions: boolean;
  searchSessions: (query: string) => void;
  sessionSearchQuery: string;
  onSelectSession: (sessionId: string) => void;
  onRenameSession: (sessionId: string, newTitle: string) => Promise<void>;
  onDeleteSession: (sessionId: string) => Promise<void>;
  activeSessionId: string | null;
  agents: WittyAssistantAgentSummary[];
  selectedAgentId: string | null;
  setSelectedAgentId: (agentId: string | null) => void;
};

type PersistedAssistantState = {
  open: boolean;
  expanded: boolean;
  sessionId: string | null;
};

const WITTY_ASSISTANT_STATE_KEY = 'witboost.witty.assistantState';
const EMPTY_AGENTS: WittyAssistantAgentSummary[] = [];

const readPersistedAssistantState = (): PersistedAssistantState => {
  const fallback = { open: false, expanded: false, sessionId: null };
  if (typeof window === 'undefined') return fallback;

  try {
    const persisted = JSON.parse(
      window.sessionStorage.getItem(WITTY_ASSISTANT_STATE_KEY) ?? '{}',
    );
    return {
      open: persisted.open === true,
      expanded: persisted.expanded === true,
      sessionId:
        typeof persisted.sessionId === 'string' ? persisted.sessionId : null,
    };
  } catch {
    return fallback;
  }
};

export function useWittyAssistant(): WittyAssistantHook {
  const wittyAssistantApi = useApi(wittyAssistantApiRef);
  const alertApi = useApi(alertApiRef);
  const { environment } = useEnvironmentsContext();
  const environmentName = environment?.name;
  const [persistedState] = useState(readPersistedAssistantState);

  const [messages, setMessages] = useState<WittyMessage[]>([]);
  const [loading, setIsLoading] = useState(persistedState.sessionId !== null);
  const [generating, setGenerating] = useState(false);
  const [statusText, setStatusText] = useState('');
  const [activeToolCalls, setActiveToolCalls] = useState<WittyToolCall[]>([]);
  const [unavailableMcpServers, setUnavailableMcpServers] = useState<
    WittyUnavailableMcpServer[]
  >([]);
  const [sessionId, setSessionId] = useState<string | null>(
    persistedState.sessionId,
  );
  const [expanded, setExpanded] = useState(persistedState.expanded);

  const [open, setOpen] = useState(persistedState.open);
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);
  const agentsQuery = useQuery<WittyAssistantAgentSummary[], Error>(
    WITTY_ASSISTANT_AGENTS_QUERY_KEY,
    () => wittyAssistantApi.listAgents(),
    {
      enabled: open,
      onError: error => handleWittyError(error, alertApi),
    },
  );
  const agents = agentsQuery.data ?? EMPTY_AGENTS;
  const defaultAgent = agents.find(agent => agent.is_default);
  const selectedAgent =
    agents.find(agent => agent.id === selectedAgentId) ?? defaultAgent;
  const activeAgentId = selectedAgent?.id ?? null;
  const selectedAgentLabel = selectedAgent?.label ?? 'WITTY';
  const agentsRef = useRef(agents);
  const chatAbortControllerRef = useRef<AbortController | null>(null);
  agentsRef.current = agents;

  // History sidebar state
  const [historyOpen, setHistoryOpen] = useState(false);
  const [sessions, setSessions] = useState<WittySessionSummary[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionsOffset, setSessionsOffset] = useState(0);
  const [hasMoreSessions, setHasMoreSessions] = useState(true);
  const [sessionSearchQuery, setSessionSearchQuery] = useState('');
  const searchQueryRef = useRef('');
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const PAGE_SIZE = 10;

  useEffect(() => {
    try {
      window.sessionStorage.setItem(
        WITTY_ASSISTANT_STATE_KEY,
        JSON.stringify({ open, expanded, sessionId }),
      );
    } catch {
      // Persistence is optional; the assistant remains usable without it.
    }
  }, [open, expanded, sessionId]);

  useEffect(() => {
    setSelectedAgentId(current => {
      if (current && agents.some(agent => agent.id === current)) return current;
      return agents.find(agent => agent.is_default)?.id ?? null;
    });
  }, [agents]);

  useEffect(() => {
    if (!persistedState.sessionId || !agentsQuery.isFetched) return;

    setIsLoading(true);
    Promise.all([
      wittyAssistantApi.getSessions(100, 0).catch(() => []),
      wittyAssistantApi.getHistory(persistedState.sessionId),
    ])
      .then(([result, history]) => {
        const persistedSession = result.find(
          session => session.session_id === persistedState.sessionId,
        );
        const responderAgentId = persistedSession?.agent_id ?? undefined;
        const availableAgents = agentsRef.current;
        const activeResponderAgentId = availableAgents.some(
          agent => agent.id === responderAgentId,
        )
          ? responderAgentId
          : availableAgents.find(agent => agent.is_default)?.id;

        setSelectedAgentId(activeResponderAgentId ?? null);
        setMessages(
          history.map(message => ({
            sender: message.type === 'human' ? 'you' : 'witty',
            contentMessage: message.content,
            ...(message.type === 'ai' && (message.agent_id ?? responderAgentId)
              ? { responderAgentId: message.agent_id ?? responderAgentId }
              : {}),
            ...(message.type === 'ai' && message.agent_label
              ? { responderLabel: message.agent_label }
              : {}),
            ...(message.type === 'ai' && message.sources
              ? { sources: message.sources }
              : {}),
            ...(message.type === 'ai' && message.tool_calls
              ? { toolCalls: message.tool_calls }
              : {}),
          })),
        );
      })
      .catch(error => {
        setSessionId(null);
        setMessages([]);
        handleWittyError(error, alertApi);
      })
      .finally(() => setIsLoading(false));
  }, [
    alertApi,
    agentsQuery.isFetched,
    persistedState.sessionId,
    wittyAssistantApi,
  ]);

  const loadSessions = useCallback(() => {
    setSessionsLoading(true);
    setSessionsOffset(0);
    const q = searchQueryRef.current || undefined;
    wittyAssistantApi
      .getSessions(PAGE_SIZE, 0, q)
      .then(result => {
        setSessions(result);
        setHasMoreSessions(result.length >= PAGE_SIZE);
        setSessionsOffset(PAGE_SIZE);
      })
      .catch(() => {
        setSessions([]);
        setHasMoreSessions(false);
      })
      .finally(() => setSessionsLoading(false));
  }, [wittyAssistantApi]);

  const loadMoreSessions = useCallback(() => {
    if (sessionsLoading || !hasMoreSessions) return;
    setSessionsLoading(true);
    const q = searchQueryRef.current || undefined;
    wittyAssistantApi
      .getSessions(PAGE_SIZE, sessionsOffset, q)
      .then(result => {
        setSessions(prev => [...prev, ...result]);
        setHasMoreSessions(result.length >= PAGE_SIZE);
        setSessionsOffset(prev => prev + PAGE_SIZE);
      })
      .catch(() => setHasMoreSessions(false))
      .finally(() => setSessionsLoading(false));
  }, [wittyAssistantApi, sessionsOffset, sessionsLoading, hasMoreSessions]);

  const searchSessions = useCallback(
    (query: string) => {
      searchQueryRef.current = query;
      setSessionSearchQuery(query);
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
      searchTimerRef.current = setTimeout(() => {
        setSessionsLoading(true);
        setSessionsOffset(0);
        wittyAssistantApi
          .getSessions(PAGE_SIZE, 0, query || undefined)
          .then(result => {
            setSessions(result);
            setHasMoreSessions(result.length >= PAGE_SIZE);
            setSessionsOffset(PAGE_SIZE);
          })
          .catch(() => {
            setSessions([]);
            setHasMoreSessions(false);
          })
          .finally(() => setSessionsLoading(false));
      }, 300);
    },
    [wittyAssistantApi],
  );

  const onSelectSession = useCallback(
    (selectedSessionId: string) => {
      const selectedSession = sessions.find(
        session => session.session_id === selectedSessionId,
      );
      const defaultAgentId = agents.find(agent => agent.is_default)?.id ?? null;
      const responderAgentId = selectedSession?.agent_id ?? defaultAgentId;
      const activeResponderAgentId = agents.some(
        agent => agent.id === responderAgentId,
      )
        ? responderAgentId
        : defaultAgentId;
      setSessionId(selectedSessionId);
      setUnavailableMcpServers([]);
      setSelectedAgentId(activeResponderAgentId);
      setIsLoading(true);
      wittyAssistantApi
        .getHistory(selectedSessionId)
        .then(history => {
          const restored: WittyMessage[] = history.map(m => {
            const messageResponderAgentId: string | undefined =
              m.agent_id ?? responderAgentId ?? undefined;
            return {
              sender: m.type === 'human' ? 'you' : 'witty',
              contentMessage: m.content,
              ...(m.type === 'ai' && messageResponderAgentId
                ? { responderAgentId: messageResponderAgentId }
                : {}),
              ...(m.type === 'ai' && m.agent_label
                ? { responderLabel: m.agent_label }
                : {}),
              ...(m.type === 'ai' && m.sources ? { sources: m.sources } : {}),
              ...(m.type === 'ai' && m.tool_calls
                ? { toolCalls: m.tool_calls }
                : {}),
            };
          });
          setMessages(restored);
        })
        .catch(error => {
          handleWittyError(error, alertApi);
        })
        .finally(() => setIsLoading(false));
    },
    [wittyAssistantApi, alertApi, agents, sessions],
  );

  const onNewMessage = useCallback(
    (message: WittyMessage) => {
      chatAbortControllerRef.current?.abort();
      const abortController = new AbortController();
      chatAbortControllerRef.current = abortController;
      setMessages(oldMessages => [...oldMessages, message]);
      setIsLoading(true);
      setGenerating(true);
      setStatusText('');
      setActiveToolCalls([]);

      let streamedReplyStarted = false;
      let streamedToolCalls: WittyToolCall[] = [];
      const pageContext = getCurrentPageContext(environmentName);

      wittyAssistantApi
        .chat(
          {
            request: {
              message: message.contentMessage,
              ...(sessionId ? { session_id: sessionId } : {}),
              ...(pageContext ? { page_context: pageContext } : {}),
              ...(activeAgentId ? { agent_id: activeAgentId } : {}),
            },
          },
          token => {
            if (abortController.signal.aborted) return;
            setIsLoading(false);
            setStatusText('');
            if (!streamedReplyStarted) {
              streamedReplyStarted = true;
              setMessages(oldMessages => [
                ...oldMessages,
                {
                  sender: 'witty',
                  contentMessage: token,
                  ...(activeAgentId ? { responderAgentId: activeAgentId } : {}),
                  responderLabel: selectedAgentLabel,
                  ...(streamedToolCalls.length
                    ? { toolCalls: streamedToolCalls }
                    : {}),
                },
              ]);
            } else {
              setMessages(oldMessages => {
                const updatedMessages = [...oldMessages];
                const lastMessage = updatedMessages[updatedMessages.length - 1];
                if (lastMessage?.sender === 'witty') {
                  updatedMessages[updatedMessages.length - 1] = {
                    ...lastMessage,
                    contentMessage: `${lastMessage.contentMessage}${token}`,
                    ...(streamedToolCalls.length
                      ? { toolCalls: streamedToolCalls }
                      : {}),
                  };
                }
                return updatedMessages;
              });
            }
          },
          status => {
            if (abortController.signal.aborted) return;
            flushSync(() => {
              setStatusText(status);
            });
          },
          abortController.signal,
          sources => {
            if (abortController.signal.aborted) return;
            setMessages(oldMessages => {
              const updatedMessages = [...oldMessages];
              const lastMessage = updatedMessages[updatedMessages.length - 1];
              if (lastMessage?.sender === 'witty') {
                updatedMessages[updatedMessages.length - 1] = {
                  ...lastMessage,
                  sources,
                };
              }
              return updatedMessages;
            });
          },
          errorMessage => {
            if (abortController.signal.aborted) return;
            streamedReplyStarted = true;
            setIsLoading(false);
            setStatusText('');
            setMessages(oldMessages => [
              ...oldMessages,
              {
                sender: 'witty',
                contentMessage: errorMessage,
                ...(activeAgentId ? { responderAgentId: activeAgentId } : {}),
                responderLabel: selectedAgentLabel,
                isFeedbackActive: false,
                isError: true,
                ...(streamedToolCalls.length
                  ? { toolCalls: streamedToolCalls }
                  : {}),
              },
            ]);
          },
          toolCall => {
            if (abortController.signal.aborted) return;
            const existingIndex = streamedToolCalls.findIndex(
              current => current.id === toolCall.id,
            );
            streamedToolCalls = [...streamedToolCalls];
            if (existingIndex === -1) {
              streamedToolCalls.push(toolCall);
            } else {
              streamedToolCalls[existingIndex] = toolCall;
            }
            setActiveToolCalls(streamedToolCalls);
            if (
              streamedToolCalls.length > 0 &&
              !streamedToolCalls.some(current => current.status === 'running')
            ) {
              setStatusText('Generating response...');
            }
            if (streamedReplyStarted) {
              setMessages(oldMessages => {
                const updatedMessages = [...oldMessages];
                const lastMessage = updatedMessages[updatedMessages.length - 1];
                if (lastMessage?.sender === 'witty') {
                  updatedMessages[updatedMessages.length - 1] = {
                    ...lastMessage,
                    toolCalls: streamedToolCalls,
                  };
                }
                return updatedMessages;
              });
            }
          },
          servers => {
            if (abortController.signal.aborted) return;
            setUnavailableMcpServers(servers);
          },
        )
        .then(response => {
          if (abortController.signal.aborted) return;
          if (!streamedReplyStarted && response.message) {
            const newMessage: WittyMessage = {
              sender: 'witty',
              contentMessage: response.message,
              ...(activeAgentId ? { responderAgentId: activeAgentId } : {}),
              responderLabel: selectedAgentLabel,
              ...(streamedToolCalls.length
                ? { toolCalls: streamedToolCalls }
                : {}),
            };
            setMessages(oldMessages => [...oldMessages, newMessage]);
          }

          if (response.session_id) {
            setSessionId(response.session_id);
          }
        })
        .catch((error: any) => {
          if (error?.name !== 'AbortError') handleWittyError(error, alertApi);
        })
        .finally(() => {
          if (chatAbortControllerRef.current === abortController) {
            chatAbortControllerRef.current = null;
            setIsLoading(false);
            setGenerating(false);
            setStatusText('');
            setActiveToolCalls([]);
          }
        });
    },
    [
      wittyAssistantApi,
      alertApi,
      sessionId,
      activeAgentId,
      selectedAgentLabel,
      environmentName,
    ],
  );

  const onStop = useCallback(() => {
    chatAbortControllerRef.current?.abort();
    chatAbortControllerRef.current = null;
    setIsLoading(false);
    setGenerating(false);
    setStatusText('');
    setActiveToolCalls([]);
  }, []);

  useEffect(() => () => chatAbortControllerRef.current?.abort(), []);

  const onNewSession = useCallback(() => {
    setSessionId(null);
    setMessages([]);
    setActiveToolCalls([]);
    setUnavailableMcpServers([]);
    setSelectedAgentId(agents.find(agent => agent.is_default)?.id ?? null);
  }, [agents]);

  const onOpenMcpConnection = useCallback((connectionId: string) => {
    window.location.assign(
      `/settings/advanced?mcp_connection=${encodeURIComponent(connectionId)}`,
    );
  }, []);

  const onRenameSession = useCallback(
    async (targetSessionId: string, newTitle: string) => {
      await wittyAssistantApi.renameSession(targetSessionId, newTitle);
      setSessions(prev =>
        prev.map(s =>
          s.session_id === targetSessionId ? { ...s, title: newTitle } : s,
        ),
      );
    },
    [wittyAssistantApi],
  );

  const onDeleteSession = useCallback(
    async (targetSessionId: string) => {
      await wittyAssistantApi.deleteSession(targetSessionId);
      setSessions(prev => prev.filter(s => s.session_id !== targetSessionId));
      if (sessionId === targetSessionId) {
        setSessionId(null);
        setMessages([]);
      }
    },
    [wittyAssistantApi, sessionId],
  );

  return {
    loading,
    generating,
    statusText,
    activeToolCalls,
    unavailableMcpServers,
    onOpenMcpConnection,
    messages,
    open,
    onNewMessage,
    onStop,
    setOpen,
    expanded,
    setExpanded,
    onNewSession,
    historyOpen,
    setHistoryOpen,
    sessions,
    sessionsLoading,
    loadSessions,
    loadMoreSessions,
    hasMoreSessions,
    searchSessions,
    sessionSearchQuery,
    onSelectSession,
    onRenameSession,
    onDeleteSession,
    activeSessionId: sessionId,
    agents,
    selectedAgentId,
    setSelectedAgentId,
  };
}
